import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { Country } from '@navio/nsdk';
import { useAppForm } from 'noddi-ui-web';
import { useState } from 'react';
import { z } from 'zod';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { getErrorCodesWithTrans } from '../../utils/getErrorCodesWithTranslations';
import { CompanyRegistrySearch } from './CompanyRegistrySearch';

const companySignupDetailsSchema = () =>
  z.object({
    organizationName: z
      .string()
      .trim()
      .min(1, t`Enter a company name`),
    organizationNumber: z
      .string()
      .trim()
      .min(1, t`Enter a company organization number`),
    departmentName: z
      .string()
      .trim()
      .min(1, t`Enter a department name`)
      .max(255, t`Use 255 characters or fewer`)
  });

export type CompanySignupDetails = z.input<ReturnType<typeof companySignupDetailsSchema>>;

type Props = {
  registryCountry?: Country;
  initialValues?: CompanySignupDetails;
  onSubmit: (values: CompanySignupDetails) => Promise<void> | void;
  submitLabel: string;
};

export const CompanySignupDetailsForm = ({ registryCountry, initialValues, onSubmit, submitLabel }: Props) => {
  const { brandEmail } = useCurrentBrand();
  const [isManualEntry, setIsManualEntry] = useState(!!initialValues);
  const form = useAppForm({
    defaultValues: initialValues ?? { organizationName: '', organizationNumber: '', departmentName: '' },
    validators: { onSubmit: companySignupDetailsSchema() },
    onSubmit: async ({ value }) => onSubmit(companySignupDetailsSchema().parse(value))
  });

  return (
    <form.AppForm>
      <form
        className='mt-5 flex flex-col gap-5'
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        {!isManualEntry && registryCountry != null ? (
          <CompanyRegistrySearch
            registryCountry={registryCountry}
            errorCodes={getErrorCodesWithTrans(brandEmail)}
            onSelect={(company) => {
              form.setFieldValue('organizationName', company.name);
              form.setFieldValue('organizationNumber', company.organizationNumber);
            }}
            onClear={() => {
              form.setFieldValue('organizationName', '');
              form.setFieldValue('organizationNumber', '');
            }}
            onSwitchToManual={() => setIsManualEntry(true)}
          />
        ) : (
          <>
            <form.AppField name='organizationName'>{(field) => <field.Input label={t`Company name`} />}</form.AppField>
            <form.AppField name='organizationNumber'>
              {(field) => <field.Input label={t`Company organization number`} inputMode='numeric' />}
            </form.AppField>
            {registryCountry != null && (
              <button
                type='button'
                onClick={() => {
                  form.setFieldValue('organizationName', '');
                  form.setFieldValue('organizationNumber', '');
                  setIsManualEntry(false);
                }}
                className='self-start text-sm text-muted-foreground underline hover:text-foreground'
              >
                <Trans>Search the company registry instead</Trans>
              </button>
            )}
          </>
        )}
        <div>
          <form.AppField name='departmentName'>
            {(field) => <field.Input label={t`Department name`} maxLength={255} />}
          </form.AppField>
          <p className='mt-2 text-sm text-muted-foreground'>
            <Trans>Name your first department, for example Oslo office. You can add more departments later.</Trans>
          </p>
        </div>
        <form.Subscribe selector={(state) => state.values}>
          {(values) => (
            <form.SubscribeButton
              className='self-end'
              label={submitLabel}
              disabledReason={
                !values.organizationName.trim()
                  ? t`Enter a company name`
                  : !values.organizationNumber.trim()
                    ? t`Enter a company organization number`
                    : !values.departmentName.trim()
                      ? t`Enter a department name`
                      : undefined
              }
            />
          )}
        </form.Subscribe>
      </form>
    </form.AppForm>
  );
};
